import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { MonitorTabs } from '../components/monitor';
import { useDarkMode } from '../hooks/useDarkMode';
import { useModalManager } from '../hooks/useModalManager';
import { useToast } from '../components/Toast';
import { useAuthStore } from '../store/authStore';
import { CSVImportModal } from '../components/CSVImportModal';
import { TaxDashboard } from '../components/tax/TaxDashboard';
import { TransactionList } from '../components/tax/TransactionList';
import { TaxCalculationResult } from '../components/tax/TaxCalculationResult';
import { LossCarryForwardInput, type PreviousYearLossEntry } from '../components/tax/LossCarryForwardInput';
import { HistoricalPricesPanel } from '../components/tax/HistoricalPricesPanel';
import {
  fetchHistoricalPricesBatch,
  clearHistoricalPriceCache,
  type PriceSnapshotsResult,
} from '../services/cryptoHistoricalPriceCache';
import { FormGenerator } from '../components/tax/FormGenerator';
import { usePortfolio } from '../hooks/usePortfolio';
import { usePositions } from '../hooks/usePositions';
import { FrenchTaxCalculator, type TaxableEvent, type TaxSummary } from '../services/frenchTaxCalculator';
import { TAX_CENTER_COLORS as TAX_COLORS } from '../config/monitorColors';

type TabType = 'dashboard' | 'import' | 'transactions' | 'calculation' | 'forms';

// V2.I.bonus.b : palette moved to src/config/monitorColors.ts as
// TAX_CENTER_COLORS (kept distinct from ACCOUNT_COLORS — sky-blue vs
// indigo). Local alias preserves call-sites that read TAX_COLORS.x.

interface TaxCenterProps {
  userMode?: 'simple' | 'expert';
}

export function TaxCenter({ userMode = 'expert' }: TaxCenterProps) {
  const { isAuthenticated } = useAuthStore();
  // Auth gate at the wrapper level — see CongressTrading for the same
  // refactor rationale (rules of hooks: hook order must stay stable).
  if (!isAuthenticated) {
    return <TaxCenterLoginGate />;
  }
  return <TaxCenterAuthenticated userMode={userMode} />;
}

function TaxCenterLoginGate() {
  const { t } = useTranslation();
  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>
        {t('tax.center.title', 'Centre Fiscal')}
      </h1>
      <p style={{ color: 'var(--muted)', marginBottom: '24px' }}>
        {t('tax.center.subtitle', 'Gérez vos obligations fiscales crypto')}
      </p>

      <div style={{
        background: 'rgba(0, 0, 0, 0.25)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '60px 20px',
        textAlign: 'center',
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: 'rgba(139, 92, 246, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <Icons.Lock size={32} color="#8b5cf6" />
        </div>
        <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>
          {t('tax.loginRequired', 'Connexion requise')}
        </h2>
        <p style={{ color: 'var(--muted)', fontSize: '14px', maxWidth: '300px', margin: '0 auto' }}>
          {t('tax.loginMessage', 'Connectez-vous pour accéder au Centre Fiscal et calculer vos impôts.')}
        </p>
      </div>
    </div>
  );
}

function TaxCenterAuthenticated({ userMode = 'expert' }: TaxCenterProps) {
  const { t } = useTranslation();
  const [isDark] = useDarkMode();
  const toast = useToast();

  // Portfolio data — `usePortfolio` and `usePositions` must run together,
  // unconditionally, on every render of this authenticated component.
  const portfolioHook = usePortfolio();
  const positionsHook = usePositions(portfolioHook.activePortfolio?.id);

  // State
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  // Single-modal page — use the shared manager for consistency with the
  // Portfolio surface, even though there's only one modal here.
  const modals = useModalManager<'csvImport'>();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [importedTransactions, setImportedTransactions] = useState<TaxableEvent[]>([]);
  const [taxSummary, setTaxSummary] = useState<TaxSummary | null>(null);

  // V2.E.UI.1 — per-event income regime overrides for reward events
  // (staking_reward, mining, airdrop). Surfaces V2.E1b's opt-in
  // `TaxableEvent.incomeRegime` to the user. Persisted to localStorage
  // so the choice survives reloads. Map keyed by event.id; absence
  // means "neutral" (calculator's default behaviour).
  const INCOME_REGIME_STORAGE_KEY = 'tax_center_income_regime_overrides';
  const [incomeRegimeOverrides, setIncomeRegimeOverrides] = useState<
    Record<string, 'PFU' | 'BNC'>
  >(() => {
    try {
      const raw = localStorage.getItem(INCOME_REGIME_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(
        INCOME_REGIME_STORAGE_KEY,
        JSON.stringify(incomeRegimeOverrides),
      );
    } catch {
      // localStorage may be unavailable (private mode) — silent.
    }
  }, [incomeRegimeOverrides]);

  const handleSetIncomeRegime = useCallback(
    (eventId: string, regime: 'PFU' | 'BNC' | null) => {
      setIncomeRegimeOverrides((prev) => {
        const next = { ...prev };
        if (regime === null) delete next[eventId];
        else next[eventId] = regime;
        return next;
      });
    },
    [],
  );

  // V2.E.UI.2 — previous-year losses surfaced from V2.E1c. List of
  // `{ year, amount }` entries persisted to localStorage. The
  // calculator filters silently (10-year window + non-positive),
  // so we keep the storage permissive and validate at the UI level
  // for the form, not the persisted data.
  const PREVIOUS_LOSSES_STORAGE_KEY = 'tax_center_previous_year_losses';
  const [previousLosses, setPreviousLosses] = useState<PreviousYearLossEntry[]>(
    () => {
      try {
        const raw = localStorage.getItem(PREVIOUS_LOSSES_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    },
  );

  useEffect(() => {
    try {
      localStorage.setItem(
        PREVIOUS_LOSSES_STORAGE_KEY,
        JSON.stringify(previousLosses),
      );
    } catch {
      // localStorage may be unavailable (private mode) — silent.
    }
  }, [previousLosses]);

  // V2.E.UI.4 — historical price snapshots state. NOT persisted to
  // localStorage at the page level (the underlying cache is — see
  // `cryptoHistoricalPriceCache`). Lifecycle is per-session: explicit
  // fetch on user click, cleared on year change.
  const [priceSnapshots, setPriceSnapshots] = useState<
    Record<string, Record<string, number>> | null
  >(null);
  const [snapshotsResult, setSnapshotsResult] = useState<PriceSnapshotsResult | null>(
    null,
  );
  const [isFetchingSnapshots, setIsFetchingSnapshots] = useState(false);

  // Reset snapshots when the user changes year — old snapshots may
  // not match the new year's events.
  useEffect(() => {
    setPriceSnapshots(null);
    setSnapshotsResult(null);
  }, [selectedYear]);

  // Convert portfolio positions to taxable events
  const portfolioEvents = useMemo(() => {
    const events: TaxableEvent[] = [];
    const tokens = portfolioHook.tokens || [];

    for (const token of tokens) {
      const positions = positionsHook.getPositionsForToken?.(token.id) || [];

      for (const pos of positions) {
        if (pos.type === 'sell') {
          events.push({
            id: pos.id,
            date: pos.date,
            type: 'sale',
            asset: token.symbol || token.id,
            quantity: pos.quantity,
            pricePerUnit: pos.price,
            totalValue: pos.quantity * pos.price,
            fees: 0,
            exchange: pos.note?.includes(':') ? pos.note.split(':')[0] : 'Manual',
          });
        } else if (pos.type === 'buy') {
          events.push({
            id: pos.id,
            date: pos.date,
            type: 'purchase',
            asset: token.symbol || token.id,
            quantity: pos.quantity,
            pricePerUnit: pos.price,
            totalValue: pos.quantity * pos.price,
            fees: 0,
            exchange: pos.note?.includes(':') ? pos.note.split(':')[0] : 'Manual',
          });
        } else if (pos.type === 'staking') {
          events.push({
            id: pos.id,
            date: pos.date,
            type: 'staking_reward',
            asset: token.symbol || token.id,
            quantity: pos.quantity,
            pricePerUnit: pos.price,
            totalValue: pos.quantity * pos.price,
            fees: 0,
            exchange: pos.staking_platform || 'Staking',
          });
        }
      }
    }

    return events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [portfolioHook.tokens, positionsHook]);

  // Combine imported and portfolio events. V2.E.UI.1 — apply
  // user-chosen incomeRegime overrides on the fly (kept out of the
  // raw event sources so the user's choice is a separate concern
  // from the data import path).
  const allEvents = useMemo(() => {
    const merged = [...portfolioEvents, ...importedTransactions]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    return merged.map((event) => {
      const override = incomeRegimeOverrides[event.id];
      return override ? { ...event, incomeRegime: override } : event;
    });
  }, [portfolioEvents, importedTransactions, incomeRegimeOverrides]);

  // Filter events by year
  const yearEvents = useMemo(() => {
    return allEvents.filter(e => new Date(e.date).getFullYear() === selectedYear);
  }, [allEvents, selectedYear]);

  // V2.E.UI.4 — build the (coinId, dateISO) pairs needed by the
  // Art. 150 VH bis formula. Only sale / swap_out / gift_sent events
  // trigger portfolio-wide valuation, and only on held assets OTHER
  // than the event's own asset (the event provides its own price).
  const requiredPricePairs = useMemo(() => {
    const pairs: Array<{ coinId: string; dateISO: string }> = [];
    const seen = new Set<string>();
    let currentHoldings = new Set<string>();
    for (const event of allEvents) {
      if (
        event.type === 'purchase'
        || event.type === 'swap_in'
        || event.type === 'gift_received'
        || event.type === 'staking_reward'
        || event.type === 'mining'
        || event.type === 'airdrop'
      ) {
        currentHoldings = new Set(currentHoldings);
        currentHoldings.add(event.asset);
      } else if (
        event.type === 'sale'
        || event.type === 'swap_out'
        || event.type === 'gift_sent'
      ) {
        const dateISO = event.date.slice(0, 10);
        for (const asset of currentHoldings) {
          if (asset === event.asset) continue;
          const key = `${asset}|${dateISO}`;
          if (seen.has(key)) continue;
          seen.add(key);
          pairs.push({ coinId: asset.toLowerCase(), dateISO });
        }
      }
    }
    return pairs;
  }, [allEvents]);

  const handleFetchHistoricalPrices = useCallback(async () => {
    if (requiredPricePairs.length === 0) return;
    setIsFetchingSnapshots(true);
    try {
      const result = await fetchHistoricalPricesBatch(requiredPricePairs);
      setPriceSnapshots(result.snapshots);
      setSnapshotsResult(result);
    } finally {
      setIsFetchingSnapshots(false);
    }
  }, [requiredPricePairs]);

  const handleClearPriceCache = useCallback(() => {
    clearHistoricalPriceCache();
    setPriceSnapshots(null);
    setSnapshotsResult(null);
  }, []);

  // Calculate taxes
  const handleCalculateTaxes = () => {
    try {
      const calculator = new FrenchTaxCalculator(allEvents);
      // V2.E.UI.2 — pass the user-entered loss carry-forward to the
      // calculator. Calculator filters out-of-window or non-positive
      // entries silently (V2.E1c), so we forward the raw list.
      const summary = calculator.calculateForYear(selectedYear, {
        previousYearLosses: previousLosses,
        // V2.E.UI.4 — pass user-fetched historical price snapshots
        // when available. `null` falls back to the calculator's
        // legacy 24h heuristic (F6 documented limitation).
        ...(priceSnapshots ? { priceSnapshots } : {}),
      });
      setTaxSummary(summary);
      setActiveTab('calculation');
      toast(t('taxCenter.calculationComplete', 'Tax calculation complete'), 'success');
    } catch (error) {
      console.error('Tax calculation error:', error);
      toast(t('taxCenter.calculationError', 'Error calculating taxes'), 'error');
    }
  };

  // Handle CSV import
  const handleCSVImport = async (importData: {
    coinId: string;
    symbol: string;
    positions: {
      type: 'buy' | 'sell' | 'mining' | 'staking';
      date: string;
      quantity: number;
      price: number;
      note?: string;
    }[];
  }[]) => {
    const newEvents: TaxableEvent[] = [];

    for (const tokenData of importData) {
      for (const pos of tokenData.positions) {
        newEvents.push({
          id: `import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          date: pos.date,
          type: pos.type === 'sell' ? 'sale' : pos.type === 'buy' ? 'purchase' : 'staking_reward',
          asset: tokenData.symbol,
          quantity: pos.quantity,
          pricePerUnit: pos.price,
          totalValue: pos.quantity * pos.price,
          fees: 0,
          exchange: pos.note?.split(':')[0] || 'Import',
        });
      }
    }

    setImportedTransactions(prev => [...prev, ...newEvents]);
    toast(
      t('taxCenter.importSuccess', '{{count}} transactions imported', { count: newEvents.length }),
      'success'
    );
  };

  // Available years
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    const currentYear = new Date().getFullYear();

    // Add years from events
    allEvents.forEach(e => years.add(new Date(e.date).getFullYear()));

    // Always include current and previous year
    years.add(currentYear);
    years.add(currentYear - 1);

    return Array.from(years).sort((a, b) => b - a);
  }, [allEvents]);

  // Tab configuration
  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'dashboard', label: t('taxCenter.tabs.dashboard', 'Dashboard'), icon: <Icons.PieChart size={18} /> },
    { id: 'import', label: t('taxCenter.tabs.import', 'Import'), icon: <Icons.Upload size={18} /> },
    { id: 'transactions', label: t('taxCenter.tabs.transactions', 'Transactions'), icon: <Icons.FileText size={18} /> },
    { id: 'calculation', label: t('taxCenter.tabs.calculation', 'Calculation'), icon: <Icons.Calculator size={18} /> },
    { id: 'forms', label: t('taxCenter.tabs.forms', 'Forms'), icon: <Icons.Download size={18} /> },
  ];

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px',
      }}>
        <div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 700,
            color: 'var(--text)',
            marginBottom: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <span style={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              padding: '8px',
              borderRadius: '12px',
              display: 'flex',
            }}>
              <Icons.Calculator size={24} style={{ color: 'white' }} />
            </span>
            {t('taxCenter.title', 'Tax Center')}
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--muted)' }}>
            {t('taxCenter.subtitle', 'Import, calculate & generate tax documents')}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* Year selector */}
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            aria-label={t('taxCenter.fiscalYearAriaLabel', 'Fiscal year')}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              background: 'var(--card)',
              color: 'var(--text)',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            {availableYears.map(year => (
              <option key={year} value={year}>
                {t('taxCenter.fiscalYear', 'Fiscal Year')} {year}
              </option>
            ))}
          </select>

          {/* Calculate button */}
          <button
            onClick={handleCalculateTaxes}
            className="btn-primary"
            style={{
              padding: '10px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Icons.Calculator size={18} />
            {t('taxCenter.calculate', 'Calculate Taxes')}
          </button>
        </div>
      </div>

      <MonitorTabs
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
        colors={TAX_COLORS}
        ariaLabel={t('taxCenter.tabs.ariaLabel', 'Tax Center sections')}
      />

      {/* Content */}
      <div style={{
        background: 'var(--card)',
        borderRadius: '16px',
        padding: '24px',
        minHeight: '500px',
      }}>
        {activeTab === 'dashboard' && (
          <TaxDashboard
            events={yearEvents}
            year={selectedYear}
            taxSummary={taxSummary}
            onCalculate={handleCalculateTaxes}
          />
        )}

        {activeTab === 'import' && (
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px',
              marginBottom: '24px',
            }}>
              {/* CSV Import Card */}
              <div
                onClick={() => modals.open('csvImport')}
                style={{
                  padding: '24px',
                  background: isDark ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)',
                  border: '2px dashed rgba(139, 92, 246, 0.3)',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s ease',
                }}
              >
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}>
                  <Icons.FileText size={32} style={{ color: 'white' }} />
                </div>
                <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: 'var(--text)' }}>
                  {t('taxCenter.import.csv', 'Import CSV')}
                </h2>
                <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '16px' }}>
                  Binance, Coinbase, Kraken, Crypto.com, KuCoin
                </p>
                <span style={{
                  padding: '6px 12px',
                  background: 'var(--accent)',
                  color: 'white',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                }}>
                  {t('taxCenter.import.selectFiles', 'Select Files')}
                </span>
              </div>

              {/* Portfolio Sync Card */}
              <div style={{
                padding: '24px',
                background: isDark ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.05)',
                border: '2px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '16px',
                textAlign: 'center',
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}>
                  <Icons.RefreshCw size={32} style={{ color: 'white' }} />
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: 'var(--text)' }}>
                  {t('taxCenter.import.portfolio', 'From Portfolio')}
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '16px' }}>
                  {portfolioEvents.length} {t('taxCenter.import.transactionsSync', 'transactions synced')}
                </p>
                <span style={{
                  padding: '6px 12px',
                  background: '#10b981',
                  color: 'white',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                }}>
                  <Icons.Check size={14} style={{ marginRight: '4px' }} />
                  {t('taxCenter.import.synced', 'Synced')}
                </span>
              </div>

              {/* Manual Entry Card */}
              <div style={{
                padding: '24px',
                background: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                border: '2px dashed rgba(59, 130, 246, 0.3)',
                borderRadius: '16px',
                cursor: 'pointer',
                textAlign: 'center',
                opacity: 0.6,
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}>
                  <Icons.Plus size={32} style={{ color: 'white' }} />
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: 'var(--text)' }}>
                  {t('taxCenter.import.manual', 'Manual Entry')}
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '16px' }}>
                  {t('taxCenter.import.addManually', 'Add transactions manually')}
                </p>
                <span style={{
                  padding: '6px 12px',
                  background: 'var(--muted)',
                  color: 'white',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                }}>
                  {t('taxCenter.comingSoon', 'Coming Soon')}
                </span>
              </div>
            </div>

            {/* Import summary */}
            <div style={{
              padding: '16px',
              background: 'var(--hover)',
              borderRadius: '12px',
            }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text)' }}>
                {t('taxCenter.import.summary', 'Import Summary')}
              </h4>
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                <div>
                  <span style={{ fontSize: '24px', fontWeight: 700, color: 'var(--accent)' }}>
                    {allEvents.length}
                  </span>
                  <span style={{ fontSize: '13px', color: 'var(--muted)', marginLeft: '8px' }}>
                    {t('taxCenter.totalTransactions', 'Total Transactions')}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>
                    {portfolioEvents.length}
                  </span>
                  <span style={{ fontSize: '13px', color: 'var(--muted)', marginLeft: '8px' }}>
                    {t('taxCenter.fromPortfolio', 'From Portfolio')}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '24px', fontWeight: 700, color: '#8b5cf6' }}>
                    {importedTransactions.length}
                  </span>
                  <span style={{ fontSize: '13px', color: 'var(--muted)', marginLeft: '8px' }}>
                    {t('taxCenter.fromImport', 'From CSV Import')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transactions' && (
          <TransactionList
            events={yearEvents}
            year={selectedYear}
            onSetIncomeRegime={handleSetIncomeRegime}
          />
        )}

        {activeTab === 'calculation' && (
          <>
            {/* V2.E.UI.4 — historical price prefetch panel. Hidden
                when the events don't trigger any portfolio-wide
                valuation (mono-asset portfolio). */}
            <HistoricalPricesPanel
              requiredPairs={requiredPricePairs.length}
              result={snapshotsResult}
              isFetching={isFetchingSnapshots}
              onFetch={handleFetchHistoricalPrices}
              onClearCache={handleClearPriceCache}
            />
            {/* V2.E.UI.2 — loss carry-forward input visible before
                the result so the user understands their previous
                losses affect the current year's taxableAmount. */}
            <LossCarryForwardInput
              losses={previousLosses}
              onChange={setPreviousLosses}
              targetYear={selectedYear}
            />
            <TaxCalculationResult
              summary={taxSummary}
              year={selectedYear}
              onRecalculate={handleCalculateTaxes}
            />
          </>
        )}

        {activeTab === 'forms' && (
          <FormGenerator
            summary={taxSummary}
            events={yearEvents}
            year={selectedYear}
          />
        )}
      </div>

      {/* CSV Import Modal */}
      <CSVImportModal
        isOpen={modals.isOpen('csvImport')}
        onClose={() => modals.close('csvImport')}
        onImport={handleCSVImport}
      />
    </div>
  );
}
